import { SEAT_RENDER_CONFIG_CACHE_MAX } from './constants'
import type { Row, RowLabelPosition, Seat } from './types'

export const trimRenderConfigCache = (cache: Map<string, Record<string, unknown>>) => {
	if (cache.size <= SEAT_RENDER_CONFIG_CACHE_MAX) {
		return
	}

	cache.clear()
}

export const cloneSerializable = <T>(value: T): T => {
	if (typeof structuredClone === 'function') {
		try {
			return structuredClone(value)
		} catch {
			// Fallback for values that contain non-cloneable structures.
		}
	}

	return JSON.parse(JSON.stringify(value)) as T
}

export const getRowLabel = (rowIndex: number) => String(rowIndex + 1)

export const getRowDisplayLabel = (row: Pick<Row, 'label'> | null | undefined, rowIndex: number) => {
	const label = row?.label != null ? String(row.label).trim() : ''
	return label || getRowLabel(rowIndex)
}

export const getRowLabelPosition = (row?: Pick<Row, 'labelPosition'> | null): RowLabelPosition => {
	return row?.labelPosition === 'end' ? 'end' : 'start'
}

export const getSeatDisplayTitle = (seat: Seat | undefined, seatIndex = 0) => {
	const custom = seat?.title != null ? String(seat.title).trim() : ''
	if (custom) {
		return custom
	}
	const parsed = Number(seat?.index)
	if (Number.isFinite(parsed) && parsed > 0) {
		return String(seat?.index)
	}
	return String(seatIndex + 1)
}

export const toSeatKey = (rowIndex: number, seatIndex: number) => `${rowIndex}-${seatIndex}`

export const parseSeatKey = (seatKey: string) => {
	const [rowPart, seatPart] = seatKey.split('-')
	const rowIndex = Number(rowPart)
	const seatIndex = Number(seatPart)
	if (!Number.isFinite(rowIndex) || !Number.isFinite(seatIndex)) {
		return null
	}
	return { rowIndex, seatIndex }
}

export type RowNumbering = {
	startNumber: number
	reverseNumbering: boolean
}

const normalizeStartNumber = (value: unknown): number => {
	const parsed = Number(value)
	if (!Number.isFinite(parsed) || parsed < 1) {
		return 1
	}
	return Math.round(parsed)
}

/**
 * Infer startNumber / reverseNumbering from existing seat.index values when a
 * row was loaded without those fields (legacy maps).
 * reverse = leftmost index > rightmost index.
 */
export const inferRowNumbering = (seats: Seat[]): RowNumbering => {
	if (!Array.isArray(seats) || seats.length === 0) {
		return { startNumber: 1, reverseNumbering: false }
	}

	const indices = seats.map((seat, seatIndex) => {
		const parsed = Number(seat.index)
		return Number.isFinite(parsed) && parsed > 0 ? parsed : seatIndex + 1
	})

	const startNumber = Math.min(...indices)
	const leftmost = indices[0]
	const rightmost = indices[indices.length - 1]
	const reverseNumbering = leftmost > rightmost

	return {
		startNumber: normalizeStartNumber(startNumber),
		reverseNumbering
	}
}

/**
 * Rewrite seat.index on a row from its startNumber / reverseNumbering settings.
 * Ensures the settings exist (infers from current indices when missing).
 */
export const applyRowSeatNumbering = (row: Row): void => {
	if (!Array.isArray(row.seats) || row.seats.length === 0) {
		row.startNumber = normalizeStartNumber(row.startNumber)
		row.reverseNumbering = !!row.reverseNumbering
		return
	}

	const hasExplicitStart = row.startNumber !== undefined && row.startNumber !== null
	const hasExplicitReverse = row.reverseNumbering !== undefined && row.reverseNumbering !== null

	if (!hasExplicitStart || !hasExplicitReverse) {
		const inferred = inferRowNumbering(row.seats)
		if (!hasExplicitStart) {
			row.startNumber = inferred.startNumber
		}
		if (!hasExplicitReverse) {
			row.reverseNumbering = inferred.reverseNumbering
		}
	}

	const startNumber = normalizeStartNumber(row.startNumber)
	const reverseNumbering = !!row.reverseNumbering
	row.startNumber = startNumber
	row.reverseNumbering = reverseNumbering

	const seatCount = row.seats.length
	row.seats.forEach((seat, seatIndex) => {
		seat.index = reverseNumbering
			? startNumber + seatCount - 1 - seatIndex
			: startNumber + seatIndex
	})
}
